import { t } from "@airange/shared/i18n-admin-controls";
import type { ButtonProps } from "antd";
import { Button, Popconfirm, Space, Tooltip, Typography } from "antd";
import type { ReactNode } from "react";
import { Fragment } from "react";
import { resolveAdminActionPresentation } from "./admin-action-icons";
import type { AdminRowActionItem } from "./admin-action-types";

export function AdminRowActions({
  actions,
  size = "small",
  emptyText = "-",
}: {
  actions: AdminRowActionItem[];
  size?: ButtonProps["size"];
  emptyText?: ReactNode;
}) {
  const normalizedActions = actions.filter(
    (action) =>
      !action.hidden && (!action.disabled || action.loading || action.onClick || action.confirm),
  );
  if (normalizedActions.length === 0) {
    return <Typography.Text type="secondary">{emptyText}</Typography.Text>;
  }

  const renderActionButton = (action: AdminRowActionItem) => {
    const presentation = resolveAdminActionPresentation(action);
    const button = (
      <Button
        variant="text"
        size={size}
        icon={
          <span style={presentation.color ? { color: presentation.color } : undefined}>
            {presentation.icon}
          </span>
        }
        danger={Boolean(action.danger)}
        disabled={Boolean(action.disabled)}
        loading={Boolean(action.loading)}
        aria-label={typeof action.label === "string" ? action.label : undefined}
        onClick={action.confirm ? undefined : action.onClick}
      />
    );

    const tooltipTitle = presentation.unresolvedLabel ? (
      <span>
        {action.label}
        {" | "}
        {presentation.unresolvedLabel}
      </span>
    ) : (
      action.label
    );

    const tooltipButton = (
      <Tooltip title={tooltipTitle}>
        <span className="admin-inline-flex">{button}</span>
      </Tooltip>
    );

    if (!action.confirm) return tooltipButton;

    return (
      <Popconfirm
        title={action.confirm.title}
        description={action.confirm.description}
        okText={action.confirm.okText}
        cancelText={t("common.actions.cancel")}
        okButtonProps={{
          danger: Boolean(action.confirm.okDanger ?? action.danger),
          loading: Boolean(action.loading),
        }}
        disabled={Boolean(action.disabled)}
        onConfirm={() => action.onClick?.()}
      >
        {tooltipButton}
      </Popconfirm>
    );
  };

  return (
    <Space size={4}>
      {normalizedActions.map((action) => (
        <Fragment key={action.key}>{renderActionButton(action)}</Fragment>
      ))}
    </Space>
  );
}
